import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import { CopyPlus, Share2, Target } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppSidebar } from '@/components/app-sidebar'
import { ClampText } from '@/components/clamp-text'
import { LeaderboardUserSheet } from '@/components/leaderboard-user-sheet'
import { SkeletonPlan } from '@/components/list-skeletons'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { PlanOutline } from '@/components/plan-outline'
import { SiteHeader } from '@/components/site-header'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { useIsMobile } from '@/hooks/use-mobile'
import { useScrollTitle } from '@/hooks/use-scroll-title'
import { CATEGORY_COLORS, categoryTextColor } from '@/lib/category-color'
import { canNativeShare, nativeShare } from '@/lib/share'
import { goalSlug } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/friends/goals/$goalId')({
  head: () => ({
    meta: [{ title: 'Goal — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

type PublicGoal = NonNullable<ReturnType<typeof useQuery<typeof api.nodes.getPublicGoalTree>>>

function RouteComponent() {
  const { t } = useTranslation()
  const { goalId: slug } = Route.useParams()
  const router = useRouter()
  const navigate = useNavigate()
  const id = slug.split('-').pop() ?? slug
  const isValidId = id.length >= 20 && /^[a-z0-9]+$/.test(id)
  const args = isValidId ? { goalId: id as Id<'nodes'> } : 'skip'
  const ownTree = useQuery(api.nodes.getGoalTree, args)
  const goal = useQuery(api.nodes.getPublicGoalTree, args)
  const { onScroll } = useScrollTitle(goal?.title ?? '')
  const back = () =>
    window.history.length > 1 ? router.history.back() : router.navigate({ to: '/today' })

  useEffect(() => {
    if (ownTree != null) navigate({ to: '/goals/$goalId', params: { goalId: slug }, replace: true })
  }, [ownTree, slug, navigate])

  const loading = isValidId && (goal === undefined || ownTree !== null)

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader
          title={goal?.title ?? ''}
          showInbox={false}
          showSearch={false}
          onBack={back}
        />
        <SiteHeader className="hidden lg:flex" showBack onBack={back} />
        <div
          // isolate: sticky plan headers stay under the floating app header.
          className="isolate flex-1 overflow-y-auto pb-36 lg:pb-24"
          onScroll={onScroll}
          data-scroll-reset
        >
          <div className="mx-auto max-w-(--page-col) px-4 pt-4 pb-4">
            {loading ? (
              <SkeletonPlan />
            ) : goal ? (
              <FriendGoal goal={goal} goalId={id as Id<'nodes'>} />
            ) : (
              <div className="flex flex-col items-center gap-3 py-24 text-center">
                <Target className="h-12 w-12 text-muted-foreground/40" />
                <p className="text-xl font-bold text-foreground">{t('goals.notFound')}</p>
                <p className="text-sm text-muted-foreground">{t('share.goalDeleted')}</p>
              </div>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

function FriendGoal({ goal, goalId }: { goal: PublicGoal; goalId: Id<'nodes'> }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const copyPublicGoal = useMutation(api.nodes.copyPublicGoal)
  const [copying, setCopying] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)

  const tasks = goal.milestones.flatMap((m) => m.targets.flatMap((tg) => tg.tasks))
  const done = tasks.filter((tk) => tk.completedAt !== null).length
  const pct = tasks.length > 0 ? Math.round((done / tasks.length) * 100) : goal.progress
  const color = CATEGORY_COLORS[goal.category.toLowerCase()] ?? '#6b7280'

  async function share() {
    const url = withShareUtm(
      `https://goaltree.ai/share/${goalSlug(goal.title, goalId)}`,
      'goal_link',
    )
    if (isMobile && canNativeShare()) {
      await nativeShare({ text: goal.title, url })
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      toast.success(t('shareCard.linkCopied'))
    } catch {}
  }

  async function usePlan() {
    setCopying(true)
    try {
      const newId = await copyPublicGoal({ goalId })
      toast.success(t('leaderboard.profile.planCopied'))
      navigate({ to: '/goals/$goalId', params: { goalId: goalSlug(goal.title, newId) } })
    } catch (err) {
      if (err instanceof ConvexError && String(err.data).includes('Free plan is limited'))
        setUpgradeOpen(true)
      else toast.error(t('leaderboard.profile.copyFailed'))
    } finally {
      setCopying(false)
    }
  }

  return (
    <>
      <p
        className="mb-1 text-xs font-semibold uppercase tracking-widest"
        style={{ color: categoryTextColor(color) }}
      >
        {t(`goals.cat.${goal.category.toLowerCase()}`, { defaultValue: goal.category })}
      </p>
      <ClampText>
        {(clamp) => (
          <h1 className={cn('text-3xl font-black leading-tight text-foreground', clamp)}>
            {goal.title}
          </h1>
        )}
      </ClampText>
      <div className="mt-3 flex items-center justify-between gap-3">
        {goal.ownerName ? (
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            className="group flex min-w-0 cursor-pointer items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
          >
            <UserAvatar
              image={goal.ownerImage}
              name={goal.ownerName}
              size={24}
              className="h-6 w-6 shrink-0 rounded-full object-cover"
            />
            <span className="truncate text-sm underline-offset-2 group-hover:underline">
              {goal.ownerName}
            </span>
          </button>
        ) : (
          <span />
        )}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={share}
            aria-label={t('common.share')}
            className="flex size-8 cursor-pointer items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Share2 className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={usePlan}
            disabled={copying}
            className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            <CopyPlus className="h-3 w-3" />
            {copying ? '…' : t('leaderboard.profile.usePlan')}
          </button>
        </div>
      </div>
      {tasks.length > 0 && (
        <>
          <div className="mt-4 flex items-center justify-between gap-2">
            <span className="shrink-0 text-xs text-muted-foreground">
              {t('goals.remaining', { count: tasks.length - done })}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">
                {t('goals.taskCount', { completed: done, total: tasks.length })}
              </span>
              <span className="shrink-0 text-xs font-bold text-primary-text">{pct}%</span>
            </div>
          </div>
          <div className="relative mt-3 h-[3px] rounded-full bg-muted">
            <div
              className="absolute left-0 top-0 h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${pct}%`,
                backgroundColor: 'var(--primary)',
                boxShadow: '0 0 8px var(--primary)',
              }}
            />
          </div>
        </>
      )}
      <div className="mt-6">
        <PlanOutline
          tracked
          sticky
          milestones={goal.milestones.map((m) => ({
            key: m._id,
            title: m.title,
            targets: m.targets.map((tg) => ({
              key: tg._id,
              title: tg.title,
              tasks: tg.tasks.map((tk) => ({
                key: tk._id,
                title: tk.title,
                description: tk.description,
                effort: tk.effort,
                done: tk.completedAt !== null,
              })),
            })),
          }))}
        />
      </div>
      <LeaderboardUserSheet
        userId={profileOpen ? goal.userId : null}
        name={goal.ownerName ?? ''}
        image={goal.ownerImage ?? null}
        onClose={() => setProfileOpen(false)}
      />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="goal-limit" />
    </>
  )
}
